import apiClient from "./axiosConfig";

// Clients = TeleOpd deployments (backend models/Tenant.js, routes/clients.js).

/** Until the production release only "beta" clients can be changed; "locked" ones are read-only. */
export type ClientStage = "beta" | "locked";
export type ClientLifecycleStatus = "active" | "disabled" | "not_working";

export interface ClientContact {
  name?: string;
  role?: string;
  email?: string;
  phone?: string;
}

/** Where the deployment runs: server + pm2 app (+ port). */
export interface ClientDeployment {
  server: string;
  app: string;
  port?: number | null;
}

/** The TeleOpd link as lists show it. `linked` = enabled and synced at least once. */
export interface ClientLink {
  linked: boolean;
  lastSyncAt: string | null;
}

export interface Client {
  _id: string;
  name: string;
  /** pm2 app name: the stable, unique handle of a deployment. */
  slug: string;
  /** Public origin of the TeleOpd deployment, e.g. https://dev.metahos.com */
  uri: string;
  /** Missing on records written before stages existed; treated as locked. */
  stage?: ClientStage;
  deployment?: ClientDeployment;
  lifecycleStatus: ClientLifecycleStatus;
  isActive: boolean;
  contacts: ClientContact[];
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  /** Sent by the list and detail endpoints only, not by writes. */
  link?: ClientLink;
}

/** Body of POST /clients and PUT /clients/:id (the server normalizes uri and phones). */
export interface ClientPayload {
  name: string;
  /** Defaults to deployment.app on create. */
  slug?: string;
  uri: string;
  deployment?: ClientDeployment | null;
  lifecycleStatus?: ClientLifecycleStatus;
  contacts?: ClientContact[];
  notes?: string;
}

export interface ClientMetrics {
  total: number;
  active: number;
  disabled: number;
  notWorking: number;
  inactiveClients: { _id: string; name: string; slug?: string; lifecycleStatus: string }[];
}

/** Minimal identity for pages that only need a client's name and handle. */
export interface ClientSummary {
  _id: string;
  name: string;
  slug?: string;
  uri?: string;
  stage?: ClientStage;
  isActive?: boolean;
  link?: ClientLink;
}

export const stageOf = (client: { stage?: ClientStage } | null | undefined): ClientStage =>
  client && client.stage === "beta" ? "beta" : "locked";

export const clientKeys = {
  all: ["clients"] as const,
  list: () => ["clients", "list"] as const,
  detail: (clientId: string) => ["clients", clientId] as const,
};

export const fetchClients = async (): Promise<Client[]> => {
  const res = await apiClient.get<Client[]>("/clients");
  return res.data;
};

export const fetchClientSummary = async (clientId: string): Promise<ClientSummary> => {
  const res = await apiClient.get<ClientSummary>(`/clients/${clientId}`);
  return res.data;
};

export const createClient = async (payload: ClientPayload): Promise<Client> => {
  const res = await apiClient.post<Client>("/clients", payload);
  return res.data;
};

export const updateClient = async (
  clientId: string,
  payload: Partial<ClientPayload>
): Promise<Client> => {
  const res = await apiClient.put<Client>(`/clients/${clientId}`, payload);
  return res.data;
};

export const setClientStatus = async (
  clientId: string,
  lifecycleStatus: ClientLifecycleStatus
): Promise<Client> => {
  const res = await apiClient.patch<Client>(`/clients/${clientId}/status`, {
    isActive: lifecycleStatus === "active",
    lifecycleStatus,
  });
  return res.data;
};

export const setClientStage = async (clientId: string, stage: ClientStage): Promise<Client> => {
  const res = await apiClient.patch<Client>(`/clients/${clientId}/stage`, { stage });
  return res.data;
};

export const deleteClient = async (clientId: string): Promise<Client> => {
  const res = await apiClient.delete<Client>(`/clients/${clientId}`);
  return res.data;
};
